import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Typography,
} from '@material-ui/core';
import './index.scss';
import CloseIconModal from 'assets/icons/CloseIconModal';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Row } from 'components/Row';
import { Col } from 'components/Col';
import { CustomTextField } from 'components/CustomTextField';
import { CustomCheckbox } from 'components/CustomCheckbox';
import useValidations from 'pages/Contratos/Fluxos/FluxoDescentralizados/hooks/useValidations';
import { diferenceMonths } from 'utils/DateUtils';
import moment from 'moment';
import { DateMaskedInput } from 'components/MaskedInputs';
import SnackbarUtils from 'utils/SnackbarUtils';
import {
  CONTRATO_ETAPAS_PARAMS_URL,
  CONTRATO_PASSOS,
  CONTRATO_FLUXO_DESCENTRALIZADO,
  ContratoStatus,
  URL_ADITIVO_PARAM,
  VersaoContratoEnum,
  TipoContrato,
  URL_ENTE_DESCENTRALIZADO,
} from 'models/contratos';
import ModalConfirm from 'pages/Contratos/components/ModalConfirm';
import { openModal } from 'store/modules/ModalActions';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { newContratoDescentralizadoEditRoute } from 'library/Routes';
import {
  CreateRouteParams,
  EditRouteParams,
} from 'pages/Contratos/Fluxos/FluxoDescentralizados/__types';
import { changeContratoEntePublicoEditingValue, closeModalEntePublicoAditivo, contratoEntePublicoDescentralizadoCreateAditivo, contratoEntePublicoDescentralizadoEditAditivo, loadContratoEntePublico } from 'store/modules/ContratosFluxoDescentralizado/EntePublico/EntePublicoActions';
import { NewAutoCompleteField } from 'components/NewAutoCompleteField';
import { loadContratoAdendos } from 'store/modules/AllContratos/AllContratosActions';
import { loadCidadesByEstadoId } from 'store/modules/LogradouroActions';

interface Cidade {
  nome: string;
}

const ModalAditivoEntePublico = () => {
  const [cidadesCarregadas, setCidadesCarregadas] = useState<{ [key: string]: boolean }>({});
  const languageValues = useSelector(
    (state: RootState) => state.languages.languageValues
  );
  const [dataInicioError, setDataInicioError] = useState('');
  const [dataFimError, setDataFimError] = useState('');
  const contratosEntePublicoReducer = useSelector(
    (state: RootState) => state.contratosDescentralizadosEntePublico
  );
  const logradouro = useSelector((state: RootState) => state.logradouro);
  const adendosAll = useSelector(
    (state: RootState) => state.allContratos.adendosAll
  );
  const adendoVersaoContratoUm = adendosAll.find(
    (adendo) => adendo.versaoContrato === 1
  );
  const idVersaoContrato = adendoVersaoContratoUm
    ? adendoVersaoContratoUm?.id
    : null;
  const {
    validDadosEntePublicoAditivo,
    desabilitaCampoEspecificoAditivoEDistrato,
    isContratoAtivoEntePublico,
    validaStatusContratoFluxo,
    desabilitaCampoCidade,
    dataInicioIsValid,
    dataFimIsValid,
    validateDataInicio,
    validateDataFim
  } = useValidations();
  const location = useLocation();
  const params = useParams<CreateRouteParams & EditRouteParams>();
  const history = useHistory();
  const dispatch = useDispatch();
  const fluxoEntePublico = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PUBLICO);

  const handleDataChange = useCallback(
    (name, value) => {
      dispatch(changeContratoEntePublicoEditingValue(name, value));
    },
    [dispatch]
  );

  const getUFByIdEstado = (idEstadoParam: number | null): string => {
    const estadoV = logradouro.estados.find((estado) => estado.id === idEstadoParam);
    return estadoV ? estadoV.sigla : '';
  };

  const getCidades = (uf: string): Cidade[] => {
    if (!uf) {
      return [];
    }
    return logradouro.cidades[uf.toLowerCase()] || [];
  };

  const handleEstadoChange = (value: { value: string, label: string }) => {
    const currValue = value ? value.value : '';
    handleDataChange('nomCidade', currValue);
  };

  const handleCidadeChange = (value: { value: string, label: string }) => {
    const currValue = value ? value.value : '';
    handleDataChange('idEstado', currValue);
  };

  const onDataInicioChanged = (value: string) => {
    const error = validateDataInicio(
      value,
      contratosEntePublicoReducer.contratoEditing.dataFim,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    setDataInicioError(error);

    if (!error) {
      setDataInicioError('');
    }

    let months: unknown = diferenceMonths(
      value,
      contratosEntePublicoReducer.contratoEditing.dataFim
    );
    if (typeof months === 'string') {
      months = parseFloat(months);
    }
    if (isNaN(months as number)) {
      months = 0;
    }
    handleDataChange('totalMeses', Math.max(months as number, 0));
  };

  const onDataFimChanged = (value: string) => {
    const error = validateDataFim(
      value,
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      {
        before: languageValues.dataFimError,
        same: languageValues.dataFimIgualDataInicioError,
      }
    );
    setDataFimError(error);

    if (!error) {
      setDataFimError('');
    }
    const dataInicioErrorValue = validateDataInicio(
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      value,
      {
        after: languageValues.dataInicioError,
        same: languageValues.dataInicioIgualDataFimError,
      }
    );
    setDataInicioError(dataInicioErrorValue);

    let months: unknown = diferenceMonths(
      contratosEntePublicoReducer.contratoEditing.dataInicio,
      value
    );
    if (typeof months === 'string') {
      months = parseFloat(months);
    }
    if (isNaN(months as number)) {
      months = 0;
    }
    handleDataChange('totalMeses', Math.max(months as number, 0));
  };

  const dataInicioValidacaoAditivoResultado = dataInicioIsValid(
    contratosEntePublicoReducer.contratoEditing.dataInicio,
    contratosEntePublicoReducer.contratoEditing.dataFim,
    {
      after: languageValues.dataInicioError,
      same: languageValues.dataInicioIgualDataFimError,
    }
  );

  const dataFimValidacaoAditivoResultado = dataFimIsValid(
    contratosEntePublicoReducer.contratoEditing.dataFim,
    contratosEntePublicoReducer.contratoEditing.dataInicio,
    {
      before: languageValues.dataFimError,
      same: languageValues.dataFimIgualDataInicioError,
    }
  );

  const fechaModalEAtualizaContrato = () => {
    history.push(
      newContratoDescentralizadoEditRoute.getURLWithParams(
        CONTRATO_FLUXO_DESCENTRALIZADO.ENTE_PUBLICO,
        CONTRATO_ETAPAS_PARAMS_URL.EDICAO,
        idVersaoContrato,
        CONTRATO_PASSOS.DOCUMENTOS
      )
    );
    dispatch(closeModalEntePublicoAditivo());
  };

  const verificaSeTemAditivo = () => {
    return adendosAll.some(
      (item) => item.versaoContrato === VersaoContratoEnum.ADITIVO
    );
  };
  const contenAditivo = verificaSeTemAditivo();

  const abrirModalCriacaoAditivo = () => {
    const fechaModal = !desabilitaCampos();
    const status = contratosEntePublicoReducer.contratoEditing.flgStatus;

    if (fechaModal) {
      fechaModalEAtualizaContrato();
      return;
    }
    if (status === ContratoStatus.EM_EDICAO && contenAditivo === true) {
      submitAditivo();
      fechaModalEAtualizaContrato();
      return;
    }
    dispatch(
      openModal(ModalConfirm, {
        title: languageValues.criacaoAditivo,
        content: languageValues.confirmarCriacaoAditivo,
        confirmLabel: languageValues.confirmar,
        confirmCallback: () => {
          submitAditivo();
        },
        modalKey: 'modalComplete',
        maxWidth: 'sm',
      })
    );
  };

  const tituloModalAditivo = () => {
    const status = contratosEntePublicoReducer.contratoEditing.flgStatus;
    const url = window.location.href;

    if (url.includes(URL_ADITIVO_PARAM.EDICAO_ADITIVO)) {
      switch (status) {
        case ContratoStatus.ENCERRADO:
        case ContratoStatus.NAO_INICIADO:
        case ContratoStatus.VIGENTE:
        case ContratoStatus.AGUARDANDO_EXTRATO:
          return `${languageValues.aditivo}`;
        default:
          return `${languageValues.editarAditivo}`;
      }
    }

    return languageValues.criacaoAditivo;
  };

  const desabilitaCampos = () => {
    const isCriacao = location.pathname.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO);
    const { flgStatus, flgAtivo } = contratosEntePublicoReducer.contratoEditing;
    if (!flgAtivo) {
      return false;
    }
    if (!isCriacao) {
      switch (flgStatus) {
        case ContratoStatus.ENCERRADO:
        case ContratoStatus.VIGENTE:
        case ContratoStatus.NAO_INICIADO:
        case ContratoStatus.AGUARDANDO_EXTRATO:
          return false;
        default:
          return true;
      }
    }
    return true;
  };

  const submitAditivo = async () => {
    const isEdicaoAditivo = location.pathname.includes(
      URL_ADITIVO_PARAM.EDICAO_ADITIVO
    );
    const isVersaoAditivo = location.pathname.includes(
      URL_ADITIVO_PARAM.VERSAO
    );
    const isCriacao = location.pathname.includes(
      URL_ADITIVO_PARAM.CADASTRO_ADITIVO
    );
    const aditivoData = {
      idContrato: idVersaoContrato,
      idEstado: contratosEntePublicoReducer.contratoEditing.idEstado,
      nomCidade: contratosEntePublicoReducer.contratoEditing.nomCidade,
      dataInicio: moment(contratosEntePublicoReducer.contratoEditing.dataInicio, 'DD MM YYYY').format('YYYY-MM-DD'),
      dataFim: moment(contratosEntePublicoReducer.contratoEditing.dataFim, 'DD MM YYYY').format('YYYY-MM-DD'),
      flgAnaliseJuridica: contratosEntePublicoReducer.contratoEditing.flgAnaliseJuridica,
      totalMeses: contratosEntePublicoReducer.contratoEditing.totalMeses,
      tipoContrato: contratosEntePublicoReducer.contratoEditing.tipoContrato,
      versaoContrato: VersaoContratoEnum.ADITIVO
    };
    const data = { ...aditivoData };

    try {
      data.tipoContrato = TipoContrato.ENTE_PUBLICO

      if (isVersaoAditivo && isEdicaoAditivo) {
        data.idContrato = idVersaoContrato;
        dispatch(
          contratoEntePublicoDescentralizadoEditAditivo(
            Number(params.idAditivo),
            data,
            () => {
              SnackbarUtils.enqueueSnackbar({
                message: languageValues.successContratoAditivoAtualizar,
                options: {
                  variant: 'success',
                },
              });
              fechaModalEAtualizaContrato();
            },
            (error) => {
              const errorMessage = error.response?.data?.message;
              SnackbarUtils.enqueueSnackbar({
                message: errorMessage,
                options: {
                  variant: 'error',
                },
              });
            }
          )
        );
      } else if (isCriacao && (contenAditivo || !contenAditivo)) {
        data.idContrato = idVersaoContrato;
        dispatch(
          contratoEntePublicoDescentralizadoCreateAditivo(
            data,
            () => {
              SnackbarUtils.enqueueSnackbar({
                message: languageValues.successContratoAditivoSalvar,
                options: {
                  variant: 'success',
                },
              });
              fechaModalEAtualizaContrato();
              dispatch(loadContratoAdendos(idVersaoContrato));
            },
            (error) => {
              const errorMessage = error.response?.data?.message;
              SnackbarUtils.enqueueSnackbar({
                message: errorMessage,
                options: {
                  variant: 'error',
                },
              });
            }
          )
        );
      }
    } catch (error) {
      SnackbarUtils.enqueueSnackbar({
        message: error,
        options: {
          variant: 'error',
        },
      });
    }
  };

  useEffect(() => {
    if (fluxoEntePublico &&
      (location.pathname.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO) ||
        location.pathname.includes(URL_ADITIVO_PARAM.MODAL_CRIACAO_ADITIVO))) {
      dispatch(loadContratoEntePublico(Number(params.id), VersaoContratoEnum.CONTRATO));
    }
    // eslint-disable-next-line 
  }, [dispatch,
    location.pathname])

  useEffect(() => {
    if (fluxoEntePublico &&
      (location.pathname.includes(URL_ADITIVO_PARAM.MODAL_EDICAO_ADITIVO) ||
        location.pathname.includes(URL_ADITIVO_PARAM.EDICAO_ADITIVO))) {
      dispatch(loadContratoEntePublico(Number(params.idAditivo), VersaoContratoEnum.ADITIVO));
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    location.pathname]);

  useEffect(() => {
    if (fluxoEntePublico &&
      (location.pathname.includes(URL_ADITIVO_PARAM.MODAL_EDICAO_ADITIVO) ||
        location.pathname.includes(URL_ADITIVO_PARAM.MODAL_CRIACAO_ADITIVO) ||
        location.pathname.includes(URL_ADITIVO_PARAM.EDICAO_ADITIVO) ||
        location.pathname.includes(URL_ADITIVO_PARAM.CADASTRO_ADITIVO)
      )) {
      const carregaCidades = (estadoId: number, estadoUf: string) => {
        if (estadoUf && !cidadesCarregadas[estadoUf]) {
          dispatch(loadCidadesByEstadoId(estadoId, estadoUf));
          setCidadesCarregadas(prev => ({ ...prev, [estadoUf]: true }));
        }
      };
      const estadoIdV = contratosEntePublicoReducer.contratoEditing.idEstado;
      const estadoUfV = getUFByIdEstado(estadoIdV);
      carregaCidades(estadoIdV, estadoUfV);
    }
    // eslint-disable-next-line 
  }, [contratosEntePublicoReducer.contratoEditing.idEstado, dispatch, location.pathname]);

  return (
    <>
      <Dialog
        open={contratosEntePublicoReducer.isModalAditivoOpen}
        aria-labelledby='form-dialog-aditivo'
        className='form-dialog-aditivo'
        TransitionComponent={Fade}
        transitionDuration={{
          enter: 350,
        }}
        PaperProps={{
          style: {
            boxShadow: 'none',
          },
        }}
      >
        <DialogTitle className='form-dialog-aditivo__title-modal'>
          <div className='form-dialog-aditivo__titleContainer'>
            <Typography className='form-dialog-aditivo__titleModal'>
              {tituloModalAditivo()}
            </Typography>
            <IconButton
              aria-label='close'
              className='form-dialog-aditivo__closeButton'
              onClick={() => fechaModalEAtualizaContrato()}
            >
              <CloseIconModal />
            </IconButton>
          </div>
          <div className='form-dialog-aditivo__titleLine'></div>
        </DialogTitle>
        <DialogContent>
          <Box className='form-dialog-aditivo__box'>
            <Card className='form-dialog-aditivo__card'>
              <Box>
                <Row>
                  <Col cols={[12, 12, 12, 12, 12]}>
                    <Typography
                      variant='h5'
                      id='dadosEntePublico'
                      className='form-dialog-aditivo__tipo-contrato-title'
                    >
                      {languageValues.cadastroContratoDadosEntePublico}
                    </Typography>
                  </Col>
                </Row>
                <Row>
                  <Col cols={[12, 6, 6, 6, 6]}>
                    <NewAutoCompleteField
                      id="estado"
                      label={languageValues.estado}
                      placeholder={
                        logradouro.loadingAllEstadosByCoops
                          ? languageValues.loadingEstados
                          : ''
                      }
                      options={logradouro.estados.map((item) => ({
                        label: item.nome,
                        value: item.id,
                        sigla: item.sigla,
                      }))}
                      value={contratosEntePublicoReducer.contratoEditing.idEstado}
                      onChange={handleEstadoChange}
                      loading={logradouro.loadingAllEstadosByCoops}
                      loadingMessage={languageValues.loadingEstados}
                      disabled={
                        !isContratoAtivoEntePublico() ||
                        !validaStatusContratoFluxo() ||
                        !desabilitaCampoEspecificoAditivoEDistrato()
                      }
                    />
                  </Col>
                  <Col cols={[12, 6, 6, 6, 6]}>
                    <NewAutoCompleteField
                      id="cidade"
                      label={languageValues.cidade}
                      placeholder={
                        logradouro.loadingCidades
                          ? languageValues.loadingCidades
                          : ''
                      }
                      options={getCidades(
                        getUFByIdEstado(
                          contratosEntePublicoReducer.contratoEditing.idEstado
                        )
                      ).map((item) => ({
                        label: item.nome,
                        value: item.nome,
                      }))}
                      value={contratosEntePublicoReducer.contratoEditing.nomCidade}
                      onChange={handleCidadeChange}
                      loading={logradouro.loadingCidades}
                      loadingMessage={languageValues.loadingCidades}
                      disabled={
                        !isContratoAtivoEntePublico() ||
                        !validaStatusContratoFluxo() ||
                        desabilitaCampoCidade(contratosEntePublicoReducer.contratoEditing.idEstado < 1) ||
                        !desabilitaCampoEspecificoAditivoEDistrato()
                      }
                    />
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 12, 12, 12]}>
                    <Typography
                      variant='h5'
                      id='dadosEntePublico'
                      className='form-dialog-aditivo__tipo-contrato-title'
                    >
                      {languageValues.cadastroContratoDadosAditivo}
                    </Typography>
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CustomTextField
                      id={'data-inicio'}
                      label={languageValues.cadastroContratoDataInicioAditivo}
                      name='dataInicio'
                      value={contratosEntePublicoReducer.contratoEditing.dataInicio}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value.trim();
                        handleDataChange('dataInicio', value);
                        if (value.length === 10) {
                          onDataInicioChanged(value);
                        } else {
                          setDataInicioError('');
                        }
                      }}
                      InputProps={{
                        inputComponent: DateMaskedInput,
                      }}
                      helperText={
                        dataInicioError ||
                        (!dataInicioValidacaoAditivoResultado.status ? dataInicioValidacaoAditivoResultado.error : '')
                      }
                      error={!dataInicioValidacaoAditivoResultado.status || !!dataInicioError}
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CustomTextField
                      id={'data-fim'}
                      label={languageValues.cadastroContratoDataFimAditivo}
                      name='dataFim'
                      value={contratosEntePublicoReducer.contratoEditing.dataFim}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value.trim();
                        handleDataChange('dataFim', value);
                        if (value.length === 10) {
                          onDataFimChanged(value);
                        } else {
                          setDataFimError('');
                        }
                      }}
                      InputProps={{
                        inputComponent: DateMaskedInput,
                      }}
                      helperText={
                        dataFimError ||
                        (!dataFimValidacaoAditivoResultado.status ? dataFimValidacaoAditivoResultado.error : '')
                      }
                      error={!dataFimValidacaoAditivoResultado.status || !!dataFimError}
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                </Row>

                <Row className='form-dialog-aditivo__row-content'>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <div className='form-dialog-aditivo__label-total-meses'>
                      {languageValues.totalMesesContratados}
                    </div>
                    <div className='form-dialog-aditivo__value-total-meses'>
                      {isNaN(
                        contratosEntePublicoReducer.contratoEditing.totalMeses
                      )
                        ? 0
                        : Number(
                          contratosEntePublicoReducer.contratoEditing.totalMeses
                        ).toString()}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col cols={[12, 12, 6, 6, 6]}>
                    <CustomCheckbox
                      id={'analise-juridica'}
                      className={'form-dialog-aditivo__analise-juridica'}
                      label={languageValues.flgAnaliseJuridicaAceite}
                      name='flgAnaliseJuridica'
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        handleDataChange(
                          'flgAnaliseJuridica',
                          event.target.checked
                        );
                      }}
                      checked={
                        contratosEntePublicoReducer.contratoEditing
                          .flgAnaliseJuridica
                      }
                      disabled={!desabilitaCampos()}
                    />
                  </Col>
                </Row>
              </Box>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions className='form-dialog-aditivo__dialog-actions'>
          <Button
            className='form-dialog-aditivo__btnClose'
            onClick={() => fechaModalEAtualizaContrato()}
          >
            {languageValues.cancelButton}
          </Button>
          <Button
            className='form-dialog-aditivo__btnSave'
            onClick={() => abrirModalCriacaoAditivo()}
            disabled={!validDadosEntePublicoAditivo()}
          >
            {languageValues.concluir}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(ModalAditivoEntePublico);

